import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Modal } from '@/components/ds';
import { PLAN_FEATURE_KEYS, type PlanFeatureKey } from '@/api/billing.api';
import { PlanFeatureLocked } from './PlanFeatureLocked';

/**
 * Opens the upgrade prompt when the API refuses a write for a feature the
 * plan does not include (api/client.ts fires `dc:plan-feature-locked`).
 *
 * Screens hide most of those buttons already; this catches the rest, so a
 * refusal is always an offer to upgrade rather than a bare error toast.
 */
export function PlanUpgradeHost() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [feature, setFeature] = useState<PlanFeatureKey | null>(null);

  useEffect(() => {
    const onLocked = (event: Event) => {
      const key = (event as CustomEvent<{ feature?: string }>).detail?.feature;
      if (!key || !(PLAN_FEATURE_KEYS as readonly string[]).includes(key)) {
        return;
      }
      setFeature(key as PlanFeatureKey);
      // The screen thought the feature was included; ask again.
      queryClient.invalidateQueries({ queryKey: ['billing', 'features'] });
    };
    window.addEventListener('dc:plan-feature-locked', onLocked);
    return () => window.removeEventListener('dc:plan-feature-locked', onLocked);
  }, [queryClient]);

  return (
    <Modal
      isOpen={feature !== null}
      onClose={() => setFeature(null)}
      title={t('planFeatures.dialogTitle')}
      icon={<Sparkles className="size-5" />}
      size="lg"
    >
      {feature && (
        <PlanFeatureLocked
          feature={feature}
          compact
          onNavigate={() => setFeature(null)}
        />
      )}
    </Modal>
  );
}
